import React, { useState } from 'react';
import { Upload, Trash2, Loader2, AlertCircle, CheckCircle, ArrowLeftRight } from 'lucide-react';
import type { DocEntry } from '../types';

const ALLOWED_EXTS = ['pdf', 'docx', 'doc', 'xlsx'];

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getFileIcon(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop();
  if (ext === 'pdf') return '\u{1F4C4}';
  if (ext === 'doc' || ext === 'docx') return '\u{1F4DD}';
  if (ext === 'xls' || ext === 'xlsx') return '\u{1F4CA}';
  return '\u{1F4CE}';
}

interface Props {
  doc: DocEntry;
  index: number;
  totalDocs: number;
  allDocs: DocEntry[];
  onFileChange: (id: number, file: File | null) => void;
  onTextChange: (id: number, text: string) => void;
  onRemove: (id: number) => void;
  onSwap: (index1: number, index2: number) => void;
  onNameChange: (id: number, name: string) => void;
  onError: (msg: string) => void;
}

export default function DocumentInput({
  doc, index, totalDocs, allDocs,
  onFileChange, onTextChange, onRemove, onSwap, onNameChange, onError,
}: Props) {
  const [uploadStatus, setUploadStatus] = useState<'loading' | 'success' | 'error' | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [swapMenuOpen, setSwapMenuOpen] = useState(false);

  const handleFile = (file: File | null) => {
    if (file) {
      const ext = file.name.toLowerCase().split('.').pop() || '';
      if (!ALLOWED_EXTS.includes(ext)) {
        onError(`Неподдерживаемый формат файла: ${file.name}. Поддерживаются: docx, doc, pdf, xlsx`);
        return;
      }
      setUploadStatus('success');
    } else {
      setUploadStatus(null);
    }
    onFileChange(doc.id, file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) handleFile(files[0]);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-visible relative">
      {/* Header */}
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="flex-shrink-0 w-6 h-6 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {index + 1}
          </span>
          <input
            type="text"
            value={doc.name}
            onChange={(e) => onNameChange(doc.id, e.target.value)}
            className="font-medium text-gray-800 bg-transparent border-none outline-none focus:ring-0 w-full min-w-0"
            placeholder={`Документ ${index + 1}`}
          />
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {totalDocs > 1 && (
            <div className="relative">
              <button
                onClick={() => {
                  if (totalDocs === 2) { onSwap(0, 1); }
                  else { setSwapMenuOpen(!swapMenuOpen); }
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-pink-600 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-pink-300 hover:bg-pink-50 transition shadow-sm"
                title={totalDocs === 2 ? 'Поменять документы местами' : 'Поменять местами с другим документом'}
              >
                <ArrowLeftRight className="w-4 h-4" />
              </button>
              {swapMenuOpen && <div className="fixed inset-0 z-10" onClick={() => setSwapMenuOpen(false)} />}
            </div>
          )}

          {swapMenuOpen && (
            <div className="absolute left-1/2 transform -translate-x-1/2 top-12 z-20 bg-white border border-gray-200 rounded-lg shadow-xl py-1 w-[280px]">
              <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100 font-medium">Поменять местами с:</div>
              {allDocs.map((other, oi) =>
                oi !== index ? (
                  <button
                    key={other.id}
                    onClick={() => { onSwap(index, oi); setSwapMenuOpen(false); }}
                    className="w-full px-3 py-2.5 text-left text-sm hover:bg-pink-50 hover:text-pink-600 transition flex items-center gap-3 border-b border-gray-50 last:border-0"
                  >
                    <span className="w-6 h-6 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">
                      {oi + 1}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className={`block truncate ${other.file || other.text ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                        {other.file ? other.file.name : other.text ? 'Текст введён' : 'Пусто'}
                      </span>
                    </span>
                  </button>
                ) : null
              )}
            </div>
          )}

          {totalDocs > 2 && (
            <button onClick={() => onRemove(doc.id)} className="p-1 text-gray-400 hover:text-red-500 transition">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="p-4">
        {/* File Upload */}
        <div className="mb-4">
          <div
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all duration-200 ${
              dragOver
                ? 'border-pink-500 bg-pink-100 scale-[1.02]'
                : doc.file
                  ? 'border-green-400 bg-green-50'
                  : 'border-gray-300 hover:border-pink-400 hover:bg-pink-50'
            }`}
          >
            <label className="block cursor-pointer">
              <input
                type="file"
                className="hidden"
                accept=".docx,.doc,.pdf,.xlsx"
                onChange={(e) => handleFile(e.target.files?.[0] || null)}
              />
              {doc.file ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2">
                    {uploadStatus === 'loading' ? (
                      <><Loader2 className="w-5 h-5 text-pink-500 animate-spin" /><span className="font-medium text-pink-600">Проверка файла...</span></>
                    ) : uploadStatus === 'error' ? (
                      <><AlertCircle className="w-5 h-5 text-red-500" /><span className="font-medium text-red-600">Ошибка загрузки</span></>
                    ) : (
                      <><CheckCircle className="w-5 h-5 text-green-500" /><span className="font-medium text-green-600">Файл готов к сравнению</span></>
                    )}
                  </div>
                  <div className={`bg-white rounded-lg p-3 border ${uploadStatus === 'error' ? 'border-red-200' : 'border-green-200'}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getFileIcon(doc.file.name)}</span>
                      <div className="text-left flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{doc.file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(doc.file.size)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : dragOver ? (
                <div className="text-pink-600 py-4">
                  <Upload className="w-8 h-8 mx-auto mb-2 animate-bounce" />
                  <span className="font-medium">Отпустите файл здесь</span>
                </div>
              ) : (
                <div className="text-gray-500 py-2">
                  <Upload className="w-8 h-8 mx-auto mb-2" />
                  <span className="text-sm font-medium">Перетащите файл сюда</span>
                  <p className="text-xs text-gray-400 mt-1">или нажмите для выбора</p>
                  <p className="text-xs text-pink-400 mt-2">docx, doc, pdf, xlsx</p>
                </div>
              )}
            </label>
          </div>
          {doc.file && (
            <button
              onClick={() => handleFile(null)}
              className="mt-2 text-xs text-red-500 hover:text-red-700 flex items-center gap-1 mx-auto"
            >
              <Trash2 className="w-3 h-3" /> Удалить файл
            </button>
          )}
        </div>

        {/* Text Input */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-600">Или вставьте текст:</label>
            {doc.text && !doc.file && (
              <button onClick={() => onTextChange(doc.id, '')} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Очистить
              </button>
            )}
          </div>
          <textarea
            value={doc.text}
            onChange={(e) => onTextChange(doc.id, e.target.value)}
            disabled={!!doc.file}
            placeholder="Вставьте текст документа..."
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none transition disabled:bg-gray-100 disabled:text-gray-400"
          />
        </div>
      </div>
    </div>
  );
}
